import React, { useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { NavLink, Link } from 'react-router-dom';
import { Translate, setLocale, getLocale } from 'react-i18nify';
import { APPLICATION_BASE_PATH, AUTHORITY, MAIN_MENU_COLLAPSE_STATE_KEY } from '../../constants';
import { showCeModal, getCaregiverProfileImage, toggleCollapseMainMenu } from '../../actions';
import { initials } from '../../utils';
import logo from '../../assets/friska-symbol-negative.svg';
import './Sidebar.scss';
import ProfileImageUploadModal from '../ProfileImageUploadModal';
import { saveState } from '../../services/localStorage';
import HasAuthority from '../HasAuthority';
import { useInbox } from '../../contexts/InboxProvider';
import CaregiverStatisticsDialog from '../Journal/CaregiverStatisticsDialog';
import useBmsVersion from '../BMSLabelling/useBmsVersion';

const Sidebar = ({
  events,
  user,
  ui,
  showCeModal,
  authToken,
  getCaregiverProfileImage,
  toggleCollapseMainMenu,
  assessments
}) => {
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [profileImageUploadVisible, setProfileImageUploadVisible] = useState(false);
  const [statisticsModalVisible, setStatisticsModalVisible] = useState(false);
  const { numActiveErrands, lastVisitedTab, numActiveAppointments } = useInbox();
  const bmsVersion = useBmsVersion();

  const handleInboxIconActive = (match, location) => {
    return (
      location.pathname === `/${APPLICATION_BASE_PATH}` ||
      location.pathname.startsWith(`/${APPLICATION_BASE_PATH}/member/`)
    );
  };

  const handleUnhandledIconActive = (match, location) => {
    return location.pathname.indexOf('unhandled') > -1;
  };

  const handleReferralIconActive = (match, location) => {
    return location.pathname.indexOf('referrals/') > -1;
  };

  const handleInactiveIconActive = (match, location) => {
    return location.pathname.indexOf('inactive-members') > -1;
  };

  const handleKioskIconActive = (match, location) => {
    return location.pathname.indexOf('kiosk') > -1;
  };

  const handleAuscultationIconActive = (match, location) => {
    return location.pathname.indexOf('auscultation') > -1;
  };

  const handleAssessmentIconActive = (match, location) => {
    return location.pathname.includes('inbox');
  };

  const handleSearchIconActive = (match, location) => {
    return location.pathname.includes('search');
  };

  const handleBroadcastIconActive = (match, location) => {
    return location.pathname.includes('broadcast');
  };

  const toggleLocale = () => {
    if (getLocale() === 'en') {
      localStorage.setItem('locale', 'sv');
      setLocale('sv');
    } else {
      localStorage.setItem('locale', 'en');
      setLocale('en');
    }

    window.location.reload();
  };

  const toggleMenu = () => {
    toggleCollapseMainMenu();
    saveState(!ui.mainMenuCollapsed, MAIN_MENU_COLLAPSE_STATE_KEY);
  };

  const toggleAnonymousMode = () => {
    document.body.classList.toggle('patient-data-hidden');
  };

  return (
    <>
      <div className={`main-menu ${ui.mainMenuCollapsed ? 'menu-collapsed' : ''}`}>
        <div
          className={`dropdown-overlay ${profileMenuOpen ? 'dropdown-overlay__active' : ''}`}
          onClick={() => setProfileMenuOpen(false)}
        ></div>
        <div className="logo">
          <NavLink to={`/${APPLICATION_BASE_PATH}`} className="logo-link">
            <img src={logo} alt="Accumbo-logga" className="sidebar__brand-logo" />
          </NavLink>
        </div>
        <ul className="menu-links">
          <li>
            <SidebarIconBadge count={events.viewingAllEvents ? events.allEventCount : events.ownEventCount} />
            <NavLink
              to={`/${APPLICATION_BASE_PATH}`}
              className="menu-link--inbox"
              activeClassName="menu-link--selected"
              isActive={handleInboxIconActive}
            >
              <span>
                <Translate value="sidebar.menu_option.inbox" />
              </span>
            </NavLink>
          </li>
          <HasAuthority authority={AUTHORITY.MEDICAL_MANAGEMENT}>
            <li>
              <SidebarIconBadge count={events.unhandledEventsCount} />
              <NavLink
                to={`/${APPLICATION_BASE_PATH}/unhandled`}
                className="menu-link--unhandled"
                activeClassName="menu-link--selected"
                isActive={handleUnhandledIconActive}
              >
                <span>
                  <Translate value="sidebar.menu_option.unhandled" />
                </span>
              </NavLink>
            </li>
          </HasAuthority>
          <li>
            <NavLink
              exact
              to={`/${APPLICATION_BASE_PATH}/members`}
              className="menu-link--patient-list"
              activeClassName="menu-link--selected"
            >
              <span>
                <Translate value="sidebar.menu_option.active_patients_list" />
              </span>
            </NavLink>
          </li>
          <li>
            <NavLink
              exact
              to={`/${APPLICATION_BASE_PATH}/referrals/all-referrals`}
              className="menu-link--referrals"
              activeClassName="menu-link--selected"
              isActive={handleReferralIconActive}
            >
              <span>
                <Translate value="sidebar.menu_option.referrals" />
              </span>
            </NavLink>
          </li>
          <li>
            <NavLink
              exact
              to={`/${APPLICATION_BASE_PATH}/inactive-members`}
              className="menu-link--inactive"
              activeClassName="menu-link--selected"
              isActive={handleInactiveIconActive}
            >
              <span>
                <Translate value="sidebar.menu_option.inactive_patients_list" />
              </span>
            </NavLink>
          </li>
          <HasAuthority authority={[AUTHORITY.NURSE, AUTHORITY.CAREGIVER]}>
            <li>
              <SidebarIconBadge count={assessments.totalRemainingKioskAssessments} />
              <NavLink
                exact
                to={`/${APPLICATION_BASE_PATH}/kiosk`}
                className="menu-link--kiosk"
                activeClassName="menu-link--selected"
                isActive={handleKioskIconActive}
              >
                <span>
                  <Translate value="sidebar.menu_option.kiosk_review" />
                </span>
              </NavLink>
            </li>
          </HasAuthority>
          <HasAuthority authority={AUTHORITY.HANDLE_AUSCULTATION}>
            <li>
              <SidebarIconBadge count={assessments.auscultationAssessments.length} />
              <NavLink
                exact
                to={`/${APPLICATION_BASE_PATH}/auscultation-assessments`}
                className="menu-link--auscultations"
                activeClassName="menu-link--selected"
                isActive={handleAuscultationIconActive}
              >
                <span>
                  <Translate value="sidebar.menu_option.auscultation_assessments" />
                </span>
              </NavLink>
            </li>
          </HasAuthority>
          <HasAuthority authority={AUTHORITY.HANDLE_NEXT_GEN_PATIENTS}>
            <li>
              <SidebarIconBadge count={numActiveErrands + numActiveAppointments} />
              <NavLink
                exact
                to={`/${APPLICATION_BASE_PATH}/inbox${lastVisitedTab === 'errands' ? '' : `/${lastVisitedTab}`}`}
                className="menu-link--inbox"
                activeClassName="menu-link--selected"
                isActive={handleAssessmentIconActive}
              >
                <span>
                  <Translate value="sidebar.menu_option.my_patients" />
                </span>
              </NavLink>
            </li>
            <li>
              <NavLink
                exact
                to={`/${APPLICATION_BASE_PATH}/search`}
                className="menu-link--search"
                activeClassName="menu-link--selected"
                isActive={handleSearchIconActive}
              >
                <span>
                  <Translate value="sidebar.menu_option.search" />
                </span>
              </NavLink>
            </li>
          </HasAuthority>
          <HasAuthority authority="inboxBroadcast">
            <li>
              <NavLink
                exact
                to={`/${APPLICATION_BASE_PATH}/broadcast`}
                className="menu-link--broadcast"
                activeClassName="menu-link--selected"
                isActive={handleBroadcastIconActive}
              >
                <span>Patientutskick</span>
              </NavLink>
            </li>
          </HasAuthority>
          <HasAuthority authority={[AUTHORITY.CAREGIVER, AUTHORITY.NURSE]}>
            <li>
              <NavLink
                to={`/${APPLICATION_BASE_PATH}/statistics`}
                className="menu-link--statistics"
                activeClassName="menu-link--selected"
              >
                <span>
                  <Translate value="sidebar.menu_option.statistics" />
                </span>
              </NavLink>
            </li>
          </HasAuthority>
        </ul>

        <div className="lower-menu">
          <div className="menu-toggler">
            <span className="pointer" onClick={toggleMenu}>
              {ui.mainMenuCollapsed ? '»' : '«'}
            </span>
          </div>
          <div
            className={`dropdown is-up ${profileMenuOpen ? 'is-active' : ''}`}
            id="profile-dropdown"
            onClick={() => setProfileMenuOpen(!profileMenuOpen)}
          >
            <div className="dropdown-trigger">
              {user.profileImage && !ui.profileImageError ? (
                <img src={ui.profileImageUrl} />
              ) : (
                <div>{user && initials(user.givenName, user.familyName)}</div>
              )}
            </div>
            <div className="dropdown-menu" id="dropdown-menu7" role="menu">
              <div className="dropdown-content">
                <div className="dropdown-item dropdown-header">
                  <div>
                    <strong>{user && `${user.givenName} ${user.familyName}`}</strong>
                    {user?.title && <p>{user.title}</p>}
                  </div>
                </div>
                <hr className="dropdown-divider" />
                <div className="dropdown-item menu-item">
                  <a
                    href="https://drive.google.com/file/d/1T4zPPZtZ08bgrfg5V_j7jdHaUHTxS70U/view"
                    className="external"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Translate value="sidebar.tutorial" />
                  </a>
                </div>
                <div className="dropdown-item menu-item">
                  <a
                    href="https://docs.google.com/document/d/18oROQMzCq7fAaiD2-9z6DwLcSCeuufJ79GwTZwxjubM/edit?usp=sharing"
                    className="external"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Translate value="sidebar.release_notes" />
                  </a>
                </div>
                <div className="dropdown-item menu-item">
                  <a
                    href="https://docs.google.com/document/d/1i1n56-n9DRcvK8L8Lud6DchWpWqY4nrPblUP5lzVRDk"
                    className="external"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Translate value="sidebar.faq_clinic" />
                  </a>
                </div>
                <div className="dropdown-item menu-item">
                  <a
                    href="https://docs.google.com/document/d/17beT9qerx7yH-q73Y6QzbU3rCICQ52nry8dYR5wvCKE"
                    className="external"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Translate value="sidebar.faq_medical" />
                  </a>
                </div>
                <div className="dropdown-item menu-item">
                  <a
                    href="https://docs.google.com/document/d/1Tr0yfit7H26venGwAdbns8vlzS-keodDEd9Mslhy80I"
                    className="external"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Translate value="sidebar.faq_support" />
                  </a>
                </div>
                <div className="dropdown-item menu-item">
                  <span onClick={toggleAnonymousMode}>
                    <Translate
                      value={`sidebar.${document.body.classList.contains('patient-data-hidden') ? 'disable' : 'enable'}_anonymous_mode`}
                    />
                  </span>
                </div>
                <HasAuthority authority={[AUTHORITY.CAREGIVER, AUTHORITY.NURSE, AUTHORITY.MEDICAL_MANAGEMENT]}>
                  <div className="dropdown-item menu-item" onClick={() => setStatisticsModalVisible(true)}>
                    <span>
                      <Translate value="sidebar.caregiver_statistics" />
                    </span>
                  </div>
                </HasAuthority>
                <div className="dropdown-item menu-item" onClick={toggleLocale}>
                  <span>
                    <Translate value="global.buttons.switch_language" />
                  </span>
                </div>
                <div className="dropdown-item menu-item" onClick={() => setProfileImageUploadVisible(true)}>
                  <span>
                    <Translate value="sidebar.change_profile_picture" />
                  </span>
                </div>
                {!bmsVersion.isLoading && bmsVersion?.data?.version && (
                  <div className="dropdown-item menu-item">
                    <Link to={`/${APPLICATION_BASE_PATH}/bms`}>
                      <span>
                        <Translate value="about_clinic.header" />
                      </span>
                    </Link>
                  </div>
                )}

                {!bmsVersion.isLoading && !bmsVersion?.data && (
                  <div className="dropdown-item menu-item" onClick={showCeModal}>
                    <span>
                      <Translate value="about_clinic.header" />
                    </span>
                  </div>
                )}

                <div className="dropdown-item menu-item">
                  <Link to={`/${APPLICATION_BASE_PATH}/logout`}>
                    <Translate value="sidebar.logout" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ProfileImageUploadModal
        user={user}
        isVisible={profileImageUploadVisible}
        onClose={() => setProfileImageUploadVisible(false)}
        authToken={authToken}
        memberGuid={user.guid}
        getProfileImage={getCaregiverProfileImage}
        hasProfileImage={user.profileImage && !ui.profileImageError}
      />
      {statisticsModalVisible && <CaregiverStatisticsDialog onClose={() => setStatisticsModalVisible(false)} />}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    authToken: state.auth.token.jwt,
    user: state.auth.token.user,
    events: state.events,
    locale: 'sv',
    ui: state.ui,
    assessments: state.assessments
  };
};

const mapActionsToProps = {
  showCeModal,
  getCaregiverProfileImage,
  toggleCollapseMainMenu
};

export default withRouter(connect(mapStateToProps, mapActionsToProps)(Sidebar));

const SidebarIconBadge = ({ count }) => {
  if (!count) {
    return null;
  }

  return (
    <span className="sidebar-notification-container">
      <span className="sidebar-notification">{count.toString()}</span>
    </span>
  );
};
